import { motion } from "framer-motion";
import {
  AlertTriangle,
  Ban,
  Clock,
  DollarSign,
  X,
  Check,
  Zap,
  ScrollText,
  Target,
  Users,
} from "lucide-react";
import CTAButton from "@/components/funnel/CTAButton";
import { LANDING } from "@/constants/testIds";

const FRUSTRATED_IMG =
  "https://images.unsplash.com/photo-1674156423391-a65ab2a435de?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHxmcnVzdHJhdGVkJTIwc3RyZXNzZWQlMjBsYXB0b3AlMjBuaWdodHxlbnwwfHx8fDE3ODMyOTUwMDZ8MA&ixlib=rb-4.1.0&q=85";

// ────────────────────────────────────────────────────────────────────────────
// 1. PAIN & AGITATION — The 48-Hour Panic
// ────────────────────────────────────────────────────────────────────────────
export function PainSection() {
  const consequences = [
    {
      icon: DollarSign,
      title: "$47,000 in inventory frozen in FBA",
      body: "Your capital is trapped. Amazon holds it until the account is reinstated — or forever if it isn't.",
    },
    {
      icon: Clock,
      title: "17 days is the average review window",
      body: "Every day your listings are down, competitors take your BSR, your ad spend goes to zero, and your reserve balance sits locked.",
    },
    {
      icon: Ban,
      title: "One weak POA = permanent ban",
      body: "You typically get 2–3 shots. Send Amazon a generic template and you burn one of them. Send them another one and you're done.",
    },
    {
      icon: AlertTriangle,
      title: "Every hour without a real plan is money you don't get back",
      body: "There is no rewind button on Amazon. The seller who submits a professional POA fast wins. The seller who panics and Googles a template loses.",
    },
  ];

  return (
    <section
      data-testid={LANDING.pain}
      className="relative py-20 md:py-32 border-t border-white/5"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        {/* Left: text */}
        <div>
          <p className="text-xs sm:text-sm uppercase tracking-[0.25em] text-[#F04632] font-bold mb-4">
            The 48-Hour Panic
          </p>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95] mb-6">
            You opened Seller Central this morning and saw those{" "}
            <span className="text-[#F04632]">two words</span>.
          </h2>
          <p className="text-lg sm:text-xl text-white/70 leading-relaxed mb-8 font-light">
            <span className="text-white font-semibold">&quot;Account Deactivated.&quot;</span>{" "}
            Your stomach dropped. Then the second wave hit — you realized every dollar you have is
            sitting inside a marketplace that just locked you out.
          </p>
          <p className="text-lg text-white/60 leading-relaxed mb-10">
            You Googled. You joined Facebook groups. You got quoted $5,000–$15,000 by an
            attorney, or handed a copy-paste template by a $500 &quot;reinstatement
            service&quot; that clearly wrote the same thing for 400 people last month.
          </p>
          <p className="text-2xl font-bold text-white leading-tight">
            None of that is what wins.{" "}
            <span className="text-[#F04632]">What wins is a real Plan of Action.</span>
          </p>
        </div>

        {/* Right: image + stat overlays */}
        <div className="relative">
          <div className="relative rounded-2xl overflow-hidden aspect-[4/5] border border-white/10 shadow-2xl">
            <img
              src={FRUSTRATED_IMG}
              alt="Deactivated Amazon seller stressed at desk"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B] via-transparent to-transparent" />
          </div>
          {/* Floating stat card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="absolute -bottom-8 -left-4 sm:-left-8 bg-[#121316] border border-[#F04632]/40 rounded-xl p-5 shadow-2xl max-w-[240px]"
          >
            <div className="flex items-center gap-2 mb-2">
              <div className="w-2 h-2 rounded-full bg-[#F04632] animate-blink" />
              <p className="text-xs uppercase tracking-widest text-[#FF7A66] font-bold">
                Right now
              </p>
            </div>
            <p className="text-4xl font-black text-white leading-none mb-1">72%</p>
            <p className="text-sm text-white/60 leading-tight">
              of first-time deactivated sellers never get reinstated because their first POA
              is wrong.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Consequence grid */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-24 md:mt-32">
        <p className="text-center text-xs sm:text-sm uppercase tracking-[0.25em] text-white/50 font-bold mb-4">
          What&apos;s actually happening while you scroll for answers
        </p>
        <h3 className="text-center text-3xl sm:text-4xl md:text-5xl font-black tracking-tighter max-w-3xl mx-auto mb-14">
          The clock started the moment Amazon deactivated you.
        </h3>
        <div className="grid sm:grid-cols-2 gap-5">
          {consequences.map((c, i) => (
            <motion.div
              key={c.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="bg-[#121316] border border-white/10 rounded-xl p-6 hover:border-[#F04632]/40 transition-colors"
            >
              <div className="w-12 h-12 rounded-lg bg-[#F04632]/10 border border-[#F04632]/30 flex items-center justify-center mb-4">
                <c.icon className="w-6 h-6 text-[#F04632]" strokeWidth={2.5} />
              </div>
              <h4 className="text-xl font-bold text-white mb-2 tracking-tight leading-tight">
                {c.title}
              </h4>
              <p className="text-white/60 leading-relaxed">{c.body}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// 2. ATTORNEY TRAP — Cost comparison
// ────────────────────────────────────────────────────────────────────────────
export function AttorneyTrap() {
  const cards = [
    {
      testId: LANDING.trapCardAttorney,
      title: "Amazon Attorney",
      price: "$5,000 – $15,000",
      subtitle: "Legal firm on retainer",
      tone: "danger",
      cons: [
        "Retainer required up front, non-refundable",
        "3–7 day response before they even look at your case",
        "Charges by the hour — every phone call meters",
        "Still uses the same POA template as everyone else",
      ],
      note: "Great for lawsuits. Wildly overkill for a POA.",
    },
    {
      testId: LANDING.trapCardAgency,
      title: "Reinstatement Mill",
      price: "$1,500 – $2,500",
      subtitle: "Generic &quot;services&quot; company",
      tone: "warning",
      cons: [
        "Copy-paste POA writer trained on 2019 policies",
        "Same paragraphs sent to 400 sellers a month",
        "No account audit — just fills in your ASIN",
        "Amazon investigators recognize the template instantly",
      ],
      note: "It&apos;s the reason your first appeal probably failed.",
    },
    {
      testId: LANDING.trapCardProtego,
      title: "Protego AI",
      price: "$197",
      subtitle: "Custom POA · Delivered in minutes",
      tone: "success",
      pros: [
        "Trained on 2,000+ real Amazon reinstatement cases",
        "Custom root-cause analysis from your Seller Central data",
        "Written in the exact 5-part structure Amazon requires",
        "One flat price. Zero upsells. Zero recurring anything.",
      ],
      note: "Same rigor as an attorney. Zero legalese. 50× cheaper.",
      highlight: true,
    },
  ];

  return (
    <section
      data-testid={LANDING.trap}
      className="relative py-20 md:py-32 border-t border-white/5 bg-[#121316]"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <p className="text-xs sm:text-sm uppercase tracking-[0.25em] text-[#F04632] font-bold mb-4">
            The three paths in front of you
          </p>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95] mb-6">
            Every deactivated seller ends up choosing one of these three doors.
          </h2>
          <p className="text-lg sm:text-xl text-white/60 leading-relaxed">
            Two of them are already priced into the reason most sellers stay deactivated forever.
            Look at the math yourself.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-5 lg:gap-6 items-stretch">
          {cards.map((c) => {
            const tone =
              c.tone === "success"
                ? "border-[#F04632]/50 bg-gradient-to-b from-[#1A1C23] to-[#0F1013] shadow-[0_0_40px_rgba(240,70,50,0.2)]"
                : c.tone === "warning"
                  ? "border-white/10 bg-[#0F1013]"
                  : "border-white/10 bg-[#0F1013]";
            return (
              <motion.div
                key={c.title}
                data-testid={c.testId}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className={`relative rounded-2xl border p-7 ${tone} ${c.highlight ? "md:-translate-y-4 md:scale-[1.02]" : ""}`}
              >
                {c.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#F04632] text-white text-[10px] uppercase tracking-[0.2em] font-black px-3 py-1.5 rounded-full whitespace-nowrap">
                    ★ The obvious choice
                  </div>
                )}
                <p className="text-xs uppercase tracking-widest text-white/50 font-bold mb-3">
                  {c.title}
                </p>
                <p
                  className={`text-4xl md:text-5xl font-black tracking-tighter mb-1 ${
                    c.highlight ? "text-[#F04632]" : "text-white"
                  }`}
                >
                  {c.price}
                </p>
                <p
                  className="text-sm text-white/50 mb-6"
                  dangerouslySetInnerHTML={{ __html: c.subtitle }}
                />
                <ul className="space-y-3 mb-6">
                  {(c.cons || c.pros).map((line, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      {c.highlight ? (
                        <Check
                          className="w-5 h-5 text-[#10B981] flex-shrink-0 mt-0.5"
                          strokeWidth={3}
                        />
                      ) : (
                        <X className="w-5 h-5 text-[#F04632]/70 flex-shrink-0 mt-0.5" strokeWidth={3} />
                      )}
                      <span className="text-sm text-white/75 leading-relaxed">{line}</span>
                    </li>
                  ))}
                </ul>
                <p
                  className={`text-xs italic pt-4 border-t border-white/10 ${
                    c.highlight ? "text-white/80" : "text-white/50"
                  }`}
                  dangerouslySetInnerHTML={{ __html: c.note }}
                />
              </motion.div>
            );
          })}
        </div>

        <div className="mt-16 text-center">
          <p className="text-sm sm:text-base text-white/60 mb-6 max-w-2xl mx-auto">
            <span className="font-bold text-white">Same outcome. Same POA quality.</span> Just a
            different price, and about 6 weeks faster.
          </p>
          <CTAButton
            testId={LANDING.trapCta}
            subtext="You&apos;re minutes away from a POA an Amazon investigator will actually read."
          >
            Save $4,803 — Get My POA for $197
          </CTAButton>
        </div>
      </div>
    </section>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// 3. FIVE FATAL MISTAKES
// ────────────────────────────────────────────────────────────────────────────
export function FiveMistakes() {
  const mistakes = [
    {
      icon: ScrollText,
      title: "The &quot;It Wasn&apos;t My Fault&quot; Trap",
      hook: "The single fastest way to get your appeal auto-rejected.",
      body: "Amazon investigators are trained to close appeals that blame the buyer, the supplier, the carrier, or a bug. Your POA has to accept responsibility — even for things that objectively weren&apos;t your fault. There&apos;s a specific frame we use for this that keeps you honest without falling into the trap.",
    },
    {
      icon: Target,
      title: "Missing the Real Root Cause",
      hook: "Amazon isn&apos;t asking what happened — they&apos;re asking why it will never happen again.",
      body: "9 out of 10 self-written POAs address the symptom (&quot;a customer complained about a used item&quot;) instead of the systemic root cause (&quot;our inbound QC never checked pre-owned units&quot;). Miss the systemic root cause and every future appeal on this account gets 3× harder.",
    },
    {
      icon: Zap,
      title: "The Vague &quot;Preventative Measures&quot; Failure",
      hook: "&quot;We will improve our processes&quot; is what gets you permabanned.",
      body: "Amazon wants a specific, dated, measurable set of policies. Not intent. If you can&apos;t point to a document, a workflow, a tool, or a checklist you&apos;ve implemented, your preventative measures section is worthless — and it&apos;s the section they actually read most closely.",
    },
    {
      icon: Clock,
      title: "Wrong Length, Wrong Structure",
      hook: "Too short = &quot;lazy.&quot; Too long = &quot;didn&apos;t read.&quot;",
      body: "Approved POAs land inside a very tight length band — and follow the 5-block structure investigators are trained to skim. Get the structure wrong and even the correct content gets rejected because the reviewer can&apos;t find what they need in the 90 seconds they spend on your case.",
    },
    {
      icon: Ban,
      title: "The &quot;I&apos;ll Just Reactivate&quot; Loop",
      hook: "The single biggest reason accounts get permabanned instead of reinstated.",
      body: "If your first POA gets rejected, submitting a slightly-tweaked version of the same document is the #1 trigger for permanent enforcement. The rule: your second submission must be structurally different from your first — and most sellers have no idea what &apos;different&apos; means to an Amazon investigator.",
    },
  ];

  return (
    <section
      data-testid={LANDING.mistakes}
      className="relative py-20 md:py-32 border-t border-white/5"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <p className="text-xs sm:text-sm uppercase tracking-[0.25em] text-[#F04632] font-bold mb-4">
            The 5 Fatal Mistakes
          </p>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95] mb-6">
            Here&apos;s exactly why the POA you were about to submit will get rejected.
          </h2>
          <p className="text-lg sm:text-xl text-white/60 leading-relaxed max-w-3xl mx-auto">
            Over the last decade, our founder has personally audited more than 2,000 rejected
            Plans of Action. They all fail for the same 5 reasons. Most sellers commit at
            least 3 of them in their very first submission.
          </p>
        </div>

        <div className="space-y-4">
          {mistakes.map((m, i) => (
            <motion.div
              key={m.title}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
              className="group relative bg-[#121316] border border-white/10 rounded-xl p-6 md:p-8 hover:border-[#F04632]/40 transition-all"
            >
              <div className="grid md:grid-cols-[auto_1fr] gap-5 md:gap-8">
                {/* Number + icon */}
                <div className="flex md:flex-col items-center md:items-start gap-4 md:min-w-[100px]">
                  <div className="text-5xl md:text-6xl font-black tracking-tighter text-[#F04632]/25 leading-none">
                    #{i + 1}
                  </div>
                  <div className="w-12 h-12 rounded-lg bg-[#F04632]/10 border border-[#F04632]/30 flex items-center justify-center">
                    <m.icon className="w-6 h-6 text-[#F04632]" strokeWidth={2.5} />
                  </div>
                </div>
                {/* Body */}
                <div>
                  <h3
                    className="text-2xl md:text-3xl font-black tracking-tight text-white mb-2 leading-tight"
                    dangerouslySetInnerHTML={{ __html: m.title }}
                  />
                  <p
                    className="text-base sm:text-lg text-[#FF7A66] font-semibold mb-3 leading-snug"
                    dangerouslySetInnerHTML={{ __html: m.hook }}
                  />
                  <p
                    className="text-white/70 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: m.body }}
                  />
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="mt-16 text-center max-w-3xl mx-auto">
          <p className="text-xl sm:text-2xl font-bold text-white mb-3 leading-tight">
            These aren&apos;t theory. These are the 5 failures we spot in the first 90 seconds
            when a seller sends us a rejected POA.
          </p>
          <p className="text-white/60 mb-10">
            Your $197 gets you a POA that avoids every single one of them — engineered from your
            exact deactivation reason and account data.
          </p>
          <CTAButton testId={LANDING.mistakesCta}>
            Fix My POA Before I Submit
          </CTAButton>
        </div>
      </div>
    </section>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// 4. SAVINGS CALCULATOR
// ────────────────────────────────────────────────────────────────────────────
export function SavingsCalculator() {
  return (
    <section
      data-testid={LANDING.savings}
      className="relative py-20 md:py-32 border-t border-white/5 bg-[#121316]"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <p className="text-xs sm:text-sm uppercase tracking-[0.25em] text-[#F04632] font-bold mb-4">
            Do the Math
          </p>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95] mb-6">
            What&apos;s <span className="text-[#F04632]">1 day of deactivation</span> actually
            costing you?
          </h2>
          <p className="text-lg text-white/60 max-w-2xl mx-auto">
            Real numbers from a mid-sized private-label seller doing $80K/month. Adjust to your
            own scale — the story doesn&apos;t change.
          </p>
        </div>

        <div className="bg-[#0F1013] border border-white/10 rounded-2xl p-6 md:p-10 shadow-2xl">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            {/* Left: Your losses */}
            <div>
              <p className="text-xs uppercase tracking-widest text-white/50 font-bold mb-4">
                Per day, deactivated
              </p>
              <ul className="space-y-4 mb-6">
                {[
                  { label: "Lost daily sales", value: "$2,666" },
                  { label: "Ad spend still burning while listings are down", value: "$180" },
                  { label: "BSR rank decay (recovery cost)", value: "$420" },
                  { label: "Frozen FBA reserve", value: "$47,200" },
                ].map((row) => (
                  <li
                    key={row.label}
                    className="flex justify-between items-center gap-4 pb-4 border-b border-white/10 last:border-b-0"
                  >
                    <span className="text-white/70 text-sm sm:text-base">{row.label}</span>
                    <span className="font-mono font-bold text-lg sm:text-xl text-[#F04632]">
                      −{row.value}
                    </span>
                  </li>
                ))}
              </ul>
              <div className="bg-[#F04632]/10 border border-[#F04632]/30 rounded-xl p-5">
                <p className="text-xs uppercase tracking-widest text-[#FF7A66] font-bold mb-1">
                  Daily damage
                </p>
                <p className="text-4xl md:text-5xl font-black tracking-tighter text-[#F04632] leading-none">
                  −$3,266<span className="text-lg text-white/50 ml-1">/day</span>
                </p>
              </div>
            </div>

            {/* Right: The $197 math */}
            <div>
              <p className="text-xs uppercase tracking-widest text-white/50 font-bold mb-4">
                What $197 buys you
              </p>
              <div className="bg-gradient-to-b from-[#1A1C23] to-[#0F1013] border border-[#10B981]/30 rounded-xl p-6 mb-6 shadow-[0_0_30px_rgba(16,185,129,0.1)]">
                <div className="text-6xl md:text-7xl font-black tracking-tighter text-white leading-none mb-2">
                  $197
                </div>
                <p className="text-white/70 text-sm sm:text-base mb-4">
                  buys a professional Plan of Action that reduces average time-to-reinstatement
                  from 17 days to about 4.
                </p>
                <div className="flex items-baseline gap-2">
                  <span className="text-xs uppercase tracking-widest text-[#10B981] font-bold">
                    You save
                  </span>
                  <span className="font-mono font-black text-3xl text-[#10B981]">
                    ~$42,458
                  </span>
                </div>
                <p className="text-xs text-white/40 mt-2">
                  vs. staying deactivated an extra 13 days
                </p>
              </div>
              <p className="text-sm text-white/50 leading-relaxed">
                Even if you value your time at zero and don&apos;t care about your FBA
                inventory — a $197 POA that gets accepted the <em>first time</em> is the highest
                ROI decision you&apos;ll make this month.
              </p>
            </div>
          </div>

          <div className="mt-10 pt-8 border-t border-white/10 text-center">
            <p className="text-xl sm:text-2xl font-bold text-white mb-6 max-w-2xl mx-auto leading-tight">
              A DIY POA is a hobby. A professional POA is a machine. What&apos;s waiting one more
              week actually costing you?
            </p>
            <CTAButton testId={LANDING.savingsCta}>
              Save $42,458 — Get My POA for $197
            </CTAButton>
          </div>
        </div>
      </div>
    </section>
  );
}
